import React, { useState, useEffect } from 'react';
import { addDoc, collection, serverTimestamp, query, onSnapshot, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Trash2, Link as LinkIcon, X } from 'lucide-react';

const GroceryDrawer = ({ isOpen, onClose, ingredients = [], staples = [], user }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [newItem, setNewItem] = useState('');
  const [catalog, setCatalog] = useState({});

  const marianosRegex = new RegExp('(?:/p/|product/)?([0-9]{12,13})(?:\\?|$)');

  const toggleItem = (item) => {
    setCheckedItems(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const formatProductName = (slug) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const addCustomItem = async (e) => {
    e.preventDefault();
    if (!user?.uid || !newItem.trim()) return;

    let finalName = newItem.trim();
    let upc = null;

    const match = finalName.match(marianosRegex);

    if (match) {
      upc = match[1];
      if (finalName.includes('http')) {
        const slugMatch = finalName.match(/\/p\/([^/]+)/);
        if (slugMatch && slugMatch[1]) {
          finalName = formatProductName(slugMatch[1]).replace(/Oz/g, 'oz');
        } else {
          finalName = `Product ${upc.slice(-4)}`;
        }
      }
    }

    try {
      await addDoc(collection(db, `users/${user.uid}/staples`), {
        name: finalName,
        upc: upc,
        createdAt: serverTimestamp()
      });
      setNewItem('');
    } catch (error) {
      console.error("Error adding staple:", error);
    }
  };

  const deleteStaple = async (id) => {
    if (window.confirm("Delete this staple?")) {
      await deleteDoc(doc(db, `users/${user.uid}/staples`, id));
    }
  };

  // 2. Fixed Linking Logic - actually running the match
  const linkIngredientToProduct = async (ingredientName, url) => {
    const match = url.match(marianosRegex);
    if (!match) {
      alert("Invalid Mariano's link! Ensure the ID is at the end.");
      return;
    }

    const upc = match[1];
    const cleanName = ingredientName.toLowerCase().trim();

    try {
      await setDoc(doc(db, `users/${user.uid}/ingredientCatalog`, cleanName), {
        upc: upc,
        linkedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error linking:", err);
    }
  };

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, `users/${user.uid}/ingredientCatalog`));
    return onSnapshot(q, (snapshot) => {
      const mapping = {};
      snapshot.docs.forEach(doc => {
        mapping[doc.id.toLowerCase()] = doc.data();
      });
      setCatalog(mapping);
    });
  }, [user]);

  const handlePrint = () => {
    const checkedStaples = staples.filter(s => checkedItems[s.id]).map(s => s.name);
    const checkedRecipeItems = ingredients.filter(item => checkedItems[item]);
    const allToPrint = [...checkedStaples, ...checkedRecipeItems];

    if (allToPrint.length === 0) {
      alert("Check some items first!");
      return;
    }

    let printDiv = document.getElementById('print-only-container') || document.createElement('div');
    printDiv.id = 'print-only-container';
    document.body.appendChild(printDiv);
    printDiv.innerHTML = `
      <h1 style="font-size: 24pt; margin-bottom: 20px;">Grocery List</h1>
      ${allToPrint.map(item => `<div style="font-size: 18pt; margin: 10px 0;">☐ ${item}</div>`).join('')}
    `;
    window.print();
  };

  return (
    <div className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 border-r border-gray-100 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      
      <div className="p-6 border-b flex justify-between items-center bg-slate-50">
        <div>
          <h2 className="text-xl font-black text-gray-800 tracking-tight">Grocery List</h2>
          <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Ready to Shop</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2"><X size={20} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        <form onSubmit={addCustomItem} className="relative group">
          <input 
            type="text"
            className="w-full p-2.5 pl-4 pr-10 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:border-green-500 transition-all"
            placeholder="Quick add staple url..."
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
          />
          <button type="submit" className="absolute right-3 top-2.5 text-gray-400 hover:text-green-600 font-bold">+</button>
        </form>

        {/* Weekly Staples Section */}
        <section className="space-y-4">
          <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1 italic">Weekly Staples</h5>
          <div className="space-y-3">
            {staples.map(staple => {
              const isLinked = !!staple.upc; // A staple is linked if it has a UPC

              return (
                <div key={staple.id} className="flex items-center gap-3 group">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 rounded-lg border-gray-300 text-green-600 transition"
                    checked={!!checkedItems[staple.id]}
                    onChange={() => toggleItem(staple.id)}
                  />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${checkedItems[staple.id] ? 'text-gray-300 line-through' : 'text-gray-700'}`}>
                      {staple.name}
                    </p>
                  </div>
                  
                  {/* Green icon for linked staples, gray for others */}
                  <div className={`p-1 rounded ${isLinked ? 'text-green-500 bg-green-50' : 'text-gray-200'}`}>
                    <LinkIcon size={14} />
                  </div>

                  <button onClick={() => deleteStaple(staple.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Recipe Ingredients Section */}
        <section className="space-y-4">
          <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1 italic">Recipe Ingredients</h5>
          <div className="space-y-3">
            {ingredients.map((item, idx) => {
              const cleanItem = item.toLowerCase().trim();
              const linkedData = catalog[cleanItem];

              return (
                <div key={idx} className="flex items-center gap-3 group">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 rounded-lg border-gray-300"
                    checked={!!checkedItems[item]}
                    onChange={() => toggleItem(item)}
                  />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${checkedItems[item] ? 'text-gray-300 line-through' : 'text-gray-700'}`}>
                      {item}
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => {
                      const url = window.prompt(`Paste Mariano's link for "${item}":`);
                      if (url) linkIngredientToProduct(item, url);
                    }}
                    className={`p-1 rounded transition-colors ${linkedData ? 'text-green-500 bg-green-50' : 'text-gray-300 hover:text-gray-600'}`}
                  >
                    <LinkIcon size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="p-6 border-t bg-gray-50 flex flex-col gap-2">
        {Object.keys(checkedItems).some(k => checkedItems[k]) && (
          <button onClick={() => setCheckedItems({})} className="text-[10px] font-bold text-red-500 uppercase tracking-widest text-center mb-2">
            Clear Checkmarks
          </button>
        )}
        <button onClick={handlePrint} className="w-full py-3 bg-white border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:shadow-sm">
          Print Checked Items
        </button>
      </div>
    </div>
  );
};

export default GroceryDrawer;